import React, { useMemo, useState } from 'react';
import './PlansRecordsView.css';

// "Records Page" — table-forward view of all permits + municipal/government
// plans for a parcel. Imported from claude.ai/design (Records Page.dc.html),
// wired to the app's real normalized plan/permit data. Supports per-section
// text filter, column sorting, and click-to-open the detail drawer.

const APPROVED_RE = /בתוקף|אוש|מאושר|פרסום אישור/;
const parseYear = (s) => {
  const m = String(s || '').match(/(\d{4})/g);
  if (!m) return null;
  const y = parseInt(m[m.length - 1], 10);
  return y >= 1900 && y <= 2100 ? y : null;
};
const parseTime = (s) => {
  if (!s) return 0;
  const m = String(s).match(/(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}`).getTime() || 0;
  return new Date(s).getTime() || 0;
};
const statusTone = (status) => (APPROVED_RE.test(status || '') ? 'approved' : 'pending');

const DocLink = ({ url }) =>
  url ? (
    <a className="rec-doc" href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
      <i className="fi fi-rr-document" /> מסמך
    </a>
  ) : (
    <span className="rec-doc rec-doc-empty">—</span>
  );

const StatusBadge = ({ status }) => {
  if (!status) return <span className="rec-muted">—</span>;
  return (
    <span className={`rec-status rec-status-${statusTone(status)}`}>
      <span className="rec-status-dot" />
      {status}
    </span>
  );
};

// Sort + filter a row list against a column config.
function useProcessedRows(rows, cols, searchKeys) {
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const processed = useMemo(() => {
    let out = rows;
    const q = filter.trim();
    if (q) out = out.filter((r) => searchKeys.map((k) => r[k]).filter(Boolean).join(' ').includes(q));
    if (sort.key) {
      const col = cols.find((c) => c.key === sort.key);
      const type = col?.sort;
      out = [...out].sort((a, b) => {
        const va = a[sort.key];
        const vb = b[sort.key];
        let cmp;
        if (type === 'date') cmp = parseTime(va) - parseTime(vb);
        else if (type === 'num') cmp = (parseFloat(String(va).replace(/[^\d.]/g, '')) || 0) - (parseFloat(String(vb).replace(/[^\d.]/g, '')) || 0);
        else cmp = String(va || '').localeCompare(String(vb || ''), 'he');
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, cols, searchKeys, filter, sort]);
  return { filter, setFilter, sort, setSort, processed };
}

function HeaderRow({ cols, sort, setSort }) {
  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  return (
    <div className="rec-tr rec-th">
      {cols.map((col, i) => {
        if (!col.label) return <div key={i} />;
        if (!col.sort) return <div key={i}>{col.label}</div>;
        const active = sort.key === col.key;
        return (
          <div key={i} className={`rec-th-sortable ${active ? 'active' : ''}`} onClick={() => onSort(col.key)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onSort(col.key); }}>
            {col.label} <span className="rec-sort-ic">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
          </div>
        );
      })}
    </div>
  );
}

function Cell({ col, row }) {
  if (col.key === '__doc') return <div className="rec-doc-cell"><DocLink url={row.url} /></div>;
  if (col.key === 'status') return <div><StatusBadge status={row.status} /></div>;
  if (col.key === 'file') return (
    <div className="rec-file">{row.file ? <span className="rec-file-dot" /> : null}<span dir="ltr">{row.file || '—'}</span></div>
  );
  const v = row[col.key];
  return <div className={col.cls || ''} dir={col.dir || undefined} title={col.ellipsis ? v : undefined}>{v || '—'}</div>;
}

function FilterInput({ value, onChange }) {
  return (
    <div className="rec-filter">
      <i className="fi fi-rr-search" />
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder="סינון לפי טקסט…" aria-label="סינון" />
    </div>
  );
}

const PERMIT_COLS = [
  { key: 'num', label: 'מספר בקשה', sort: 'num', cls: 'rec-num', dir: 'ltr' },
  { key: 'file', label: 'תיק בניין', sort: 'str' },
  { key: 'applicant', label: 'מבקש', sort: 'str', cls: 'rec-applicant', ellipsis: true },
  { key: 'date', label: 'תאריך הגשה', sort: 'date', cls: 'rec-date', dir: 'ltr' },
  { key: 'status', label: 'סטטוס', sort: 'str' },
  { key: '__doc', label: '' },
];
const CITY_COLS = [
  { key: 'title', label: 'שם התכנית', sort: 'str', cls: 'rec-plan-title', ellipsis: true },
  { key: 'type', label: 'סוג', sort: 'str', cls: 'rec-muted' },
  { key: 'num', label: 'מספר', sort: 'str', cls: 'rec-date', dir: 'ltr' },
  { key: 'date', label: 'תאריך', sort: 'date', cls: 'rec-date', dir: 'ltr' },
  { key: 'status', label: 'סטטוס', sort: 'str' },
  { key: '__doc', label: '' },
];
const GOV_COLS = [
  { key: 'title', label: 'שם התכנית', sort: 'str', cls: 'rec-plan-title', ellipsis: true },
  { key: 'region', label: 'מרחב', sort: 'str', cls: 'rec-muted' },
  { key: 'type', label: 'סוג', sort: 'str', cls: 'rec-muted' },
  { key: 'num', label: 'מספר', sort: 'str', cls: 'rec-date', dir: 'ltr' },
  { key: 'date', label: 'תאריך', sort: 'date', cls: 'rec-date', dir: 'ltr' },
  { key: 'status', label: 'סטטוס', sort: 'str' },
  { key: '__doc', label: '' },
];

function PermitsSection({ permits, municipality, gush, helka, onOpenPlan }) {
  const { filter, setFilter, sort, setSort, processed } = useProcessedRows(permits, PERMIT_COLS, ['num', 'file', 'applicant', 'status']);
  const insight = useMemo(() => {
    if (!permits.length) return null;
    const years = permits.map((p) => parseYear(p.date)).filter(Boolean);
    const files = new Set(permits.map((p) => p.file).filter(Boolean));
    const latest = Math.max(0, ...permits.map((p) => parseTime(p.date)));
    const pending = permits.filter((p) => statusTone(p.status) === 'pending').length;
    return {
      yearRange: years.length ? `${Math.min(...years)} – ${Math.max(...years)}` : '—',
      files: files.size,
      latest: latest ? new Date(latest).toLocaleDateString('he-IL') : '—',
      pending,
      approved: permits.length - pending,
    };
  }, [permits]);
  const currentYear = new Date().getFullYear();
  const hasRecent = permits.some((p) => parseYear(p.date) >= currentYear - 7);
  if (!permits.length) return null;

  return (
    <section className="rec-section">
      <div className="rec-head">
        <div className="rec-head-title">
          <span className="rec-ico"><i className="fi fi-rr-bank" /></span>
          <div>
            <div className="rec-title">בקשות והיתרים{municipality ? ` · ${municipality}` : ''}</div>
            <div className="rec-subtitle">כל {permits.length} הבקשות לכתובת זו{(gush || helka) ? ` · גוש ${gush || '—'} חלקה ${helka || '—'}` : ''}</div>
          </div>
        </div>
        <FilterInput value={filter} onChange={setFilter} />
      </div>

      {insight && (
        <div className="rec-insight">
          <div className="rec-insight-cell"><span className="rec-insight-label">טווח שנים</span><span className="rec-insight-value rec-ltr" dir="ltr">{insight.yearRange}</span></div>
          <div className="rec-insight-cell"><span className="rec-insight-label">תיקי בניין</span><span className="rec-insight-value">{insight.files} תיקים</span></div>
          <div className="rec-insight-cell"><span className="rec-insight-label">בקשה אחרונה</span><span className="rec-insight-value rec-ltr" dir="ltr">{insight.latest}</span></div>
          <div className="rec-insight-cell"><span className="rec-insight-label">סטטוס</span><span className={`rec-insight-value ${insight.pending ? 'rec-amber' : 'rec-green'}`}>{insight.pending ? `${insight.pending} בתהליך` : `${insight.approved} מאושרות`}</span></div>
        </div>
      )}

      <div className="rec-table rec-table-permits">
        <HeaderRow cols={PERMIT_COLS} sort={sort} setSort={setSort} />
        {processed.map((p, i) => {
          const recent = parseYear(p.date) >= currentYear - 7;
          return (
            <div className={`rec-tr ${recent ? 'rec-recent' : ''} ${onOpenPlan && p._plan ? 'rec-clickable' : ''}`} key={i}
              role={onOpenPlan && p._plan ? 'button' : undefined} tabIndex={onOpenPlan && p._plan ? 0 : undefined}
              onClick={() => onOpenPlan && p._plan && onOpenPlan(p._plan)}
              onKeyDown={(e) => { if ((e.key === 'Enter') && onOpenPlan && p._plan) onOpenPlan(p._plan); }}>
              {PERMIT_COLS.map((col, ci) => <Cell key={ci} col={col} row={p} />)}
            </div>
          );
        })}
        <div className="rec-total">
          <span>סה"כ: {processed.length} רשומות{processed.length !== permits.length ? ` (מתוך ${permits.length})` : ''}</span>
          {hasRecent && <span className="rec-legend"><span className="rec-legend-swatch" /> מודגש = בקשה מ-7 השנים האחרונות</span>}
        </div>
      </div>
    </section>
  );
}

function PlansSection({ plans, title, cols, gridClass, icon, onOpenPlan }) {
  const { filter, setFilter, sort, setSort, processed } = useProcessedRows(plans, cols, ['title', 'type', 'num', 'status', 'region']);
  const [open, setOpen] = useState(false);
  const COLLAPSED = 5;
  if (!plans.length) return null;
  const visible = open ? processed : processed.slice(0, COLLAPSED);
  const hidden = processed.length - visible.length;

  return (
    <section className="rec-section">
      <div className="rec-head">
        <div className="rec-head-title">
          <span className="rec-ico"><i className={`fi ${icon}`} /></span>
          <div className="rec-title">{title}</div>
          <span className="rec-count-badge">{plans.length}</span>
        </div>
        <FilterInput value={filter} onChange={setFilter} />
      </div>
      <div className={`rec-table ${gridClass}`}>
        <HeaderRow cols={cols} sort={sort} setSort={setSort} />
        {visible.map((p, i) => (
          <div className={`rec-tr ${onOpenPlan && p._plan ? 'rec-clickable' : ''}`} key={i}
            role={onOpenPlan && p._plan ? 'button' : undefined} tabIndex={onOpenPlan && p._plan ? 0 : undefined}
            onClick={() => onOpenPlan && p._plan && onOpenPlan(p._plan)}
            onKeyDown={(e) => { if ((e.key === 'Enter') && onOpenPlan && p._plan) onOpenPlan(p._plan); }}>
            {cols.map((col, ci) => <Cell key={ci} col={col} row={p} />)}
          </div>
        ))}
      </div>
      {(hidden > 0 || open) && (
        <button type="button" className="rec-toggle" onClick={() => setOpen((o) => !o)}>
          {open ? 'הצג פחות' : `הצג עוד ${hidden} תוכניות`}
        </button>
      )}
    </section>
  );
}

export default function PlansRecordsView({ address, gush, helka, permits = [], cityPlans = [], govPlans = [], municipality, onOpenPlan }) {
  if ((permits.length + cityPlans.length + govPlans.length) === 0) return null;
  return (
    <div className="plans-records">
      <div className="rec-page-head">
        <div>
          <h3 className="rec-page-title">בקשות, היתרים ותוכניות</h3>
          {(address || gush || helka) && (
            <div className="rec-page-sub">{address || ''}{(gush || helka) ? ` · גוש ${gush || '—'} · חלקה ${helka || '—'}` : ''}</div>
          )}
        </div>
        <div className="rec-stats">
          <div className="rec-stat"><div className="rec-stat-n">{permits.length}</div><div className="rec-stat-l">בקשות והיתרים</div></div>
          <div className="rec-stat"><div className="rec-stat-n">{cityPlans.length}</div><div className="rec-stat-l">תוכניות עירוניות</div></div>
          <div className="rec-stat"><div className="rec-stat-n">{govPlans.length}</div><div className="rec-stat-l">תוכניות ממשלתיות</div></div>
        </div>
      </div>

      <PermitsSection permits={permits} municipality={municipality} gush={gush} helka={helka} onOpenPlan={onOpenPlan} />
      <PlansSection plans={cityPlans} title={`תוכניות${municipality ? ` · ${municipality}` : ' עירוניות'}`} cols={CITY_COLS} gridClass="rec-table-city" icon="fi-rr-document" onOpenPlan={onOpenPlan} />
      <PlansSection plans={govPlans} title="תוכניות ממשלתיות" cols={GOV_COLS} gridClass="rec-table-gov" icon="fi-rr-bank" onOpenPlan={onOpenPlan} />
    </div>
  );
}
